// apps/web/src/services/auth.service.ts
//
// Cognito OAuth + token storage. Starts as a PLACEHOLDER -- the env
// loader calls `configure()` once env.json is hydrated.
//
// `allowUsernamePasswordAuth: false` on the placeholder so the
// LoginScreen does not render an interactive form against the
// placeholder client id. `isConfigured()` flips to true only after
// `configure()` runs.

import { decodeJwt } from 'jose';

const STORAGE_PREFIX = 'breeze:auth';

export interface CognitoConfig {
  readonly userPoolId: string;
  readonly clientId: string;
  readonly issuerUrl: string;
  readonly oauthDomain: string;
  readonly allowUsernamePasswordAuth: boolean;
}

export interface IdTokenClaims {
  readonly sub?: string;
  readonly email?: string;
  readonly 'cognito:username'?: string;
  readonly exp?: number;
  readonly [key: string]: unknown;
}

export interface Session {
  readonly idToken: string;
  readonly accessToken: string;
  readonly refreshToken: string | null;
  readonly idTokenClaims: IdTokenClaims;
}

const PLACEHOLDER_CONFIG: CognitoConfig = {
  userPoolId: 'placeholder-pool',
  clientId: 'placeholder-client',
  issuerUrl: 'https://issuer.placeholder.invalid',
  oauthDomain: 'https://oauth.placeholder.invalid',
  allowUsernamePasswordAuth: false,
};

class AuthService {
  private config: CognitoConfig = PLACEHOLDER_CONFIG;
  private configured = false;
  private session: Session | null = null;

  configure(config: CognitoConfig): void {
    this.config = config;
    this.configured = true;
    this.session = this.readSessionFromStorage();
  }

  isConfigured(): boolean {
    return this.configured;
  }

  getCognitoConfig(): CognitoConfig {
    return this.config;
  }

  getSession(): Session | null {
    return this.session ?? this.readSessionFromStorage();
  }

  isAuthenticated(): boolean {
    const session = this.getSession();
    if (!session) return false;
    const exp = session.idTokenClaims.exp;
    if (typeof exp !== 'number') return true;
    return exp * 1000 > Date.now();
  }

  async ensureSession(): Promise<boolean> {
    return this.isAuthenticated();
  }

  setSession(tokens: {
    idToken: string;
    accessToken: string;
    refreshToken: string | null;
  }): void {
    const claims = decodeJwt(tokens.idToken) as IdTokenClaims;
    this.session = { ...tokens, idTokenClaims: claims };
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(`${STORAGE_PREFIX}:idToken`, tokens.idToken);
    window.localStorage.setItem(`${STORAGE_PREFIX}:accessToken`, tokens.accessToken);
    if (tokens.refreshToken) {
      window.localStorage.setItem(`${STORAGE_PREFIX}:refreshToken`, tokens.refreshToken);
    } else {
      window.localStorage.removeItem(`${STORAGE_PREFIX}:refreshToken`);
    }
  }

  signOut(): void {
    this.session = null;
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(`${STORAGE_PREFIX}:idToken`);
    window.localStorage.removeItem(`${STORAGE_PREFIX}:accessToken`);
    window.localStorage.removeItem(`${STORAGE_PREFIX}:refreshToken`);
    window.location.assign('/login');
  }

  startGoogleSignIn(): void {
    if (!this.configured) {
      throw new Error('Auth service is still on placeholder config.');
    }
    const redirectUri = `${window.location.origin}/auth/callback`;
    const url = new URL(`${this.config.oauthDomain.replace(/\/+$/, '')}/oauth2/authorize`);
    url.searchParams.set('identity_provider', 'Google');
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('client_id', this.config.clientId);
    url.searchParams.set('redirect_uri', redirectUri);
    url.searchParams.set('scope', 'openid email profile');
    window.location.assign(url.toString());
  }

  private readSessionFromStorage(): Session | null {
    if (typeof window === 'undefined') return null;
    const idToken = window.localStorage.getItem(`${STORAGE_PREFIX}:idToken`);
    const accessToken = window.localStorage.getItem(`${STORAGE_PREFIX}:accessToken`);
    const refreshToken = window.localStorage.getItem(`${STORAGE_PREFIX}:refreshToken`);
    if (!idToken || !accessToken) return null;
    try {
      const claims = decodeJwt(idToken) as IdTokenClaims;
      return { idToken, accessToken, refreshToken, idTokenClaims: claims };
    } catch {
      return null;
    }
  }
}

export const authService = new AuthService();
